import { ReactNode, memo, useCallback, useState } from "react";
import { mdiChevronDown } from "@mdi/js";
import {
  Box,
  Collapse,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
  useTheme,
} from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

export interface MiExpandableBoxProps {
  title: ReactNode;
  children: ReactNode;
  rightHeaderContent?: ReactNode;
  defaultExpanded?: boolean;
  unmountOnExit?: boolean;
  titleProps?: TypographyProps;
  sx?: SxProps<Theme>;
}

export const MiExpandableBox = memo(
  ({
    title,
    rightHeaderContent,
    children,
    defaultExpanded,
    unmountOnExit,
    titleProps,
    sx,
  }: MiExpandableBoxProps) => {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = useState(Boolean(defaultExpanded));

    const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);

    return (
      <Box px={2} pb={2} sx={sx}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={handleToggle}
        >
          <Typography
            color="bluegrey.dark"
            fontWeight="600"
            fontSize={12}
            {...titleProps}
          >
            {title}
          </Typography>
          <Box display="flex">
            {rightHeaderContent}
            <MiIcon
              path={mdiChevronDown}
              color={theme.palette.grey[600]}
              style={{
                transition: "transform 0.2s ease-in-out",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
        </Box>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit={unmountOnExit}>
          {children}
        </Collapse>
      </Box>
    );
  }
);
